import { Button as MuiButton, CircularProgress, Tooltip } from "@mui/material";
import { useDesignTokens } from "../../../theme/useDesignTokens";

/**
 * Common Button Component
 * Supports multiple variants, sizes, and states
 * Abstracts Material UI to minimize upgrade impacts
 */
const Button = ({
  variant = "primary",
  size = "medium",
  children,
  loading = false,
  disabled = false,
  startIcon = null,
  endIcon = null,
  fullWidth = false,
  onClick,
  type = "button",
  sx = {},
  className = "",
  isNotAllowed = false,
  tooltip = "",
  slotProps = {},
  ...props
}) => {
  const { colors, font, radius } = useDesignTokens();
  const prMain = colors?.primary?.main;
  const prText = colors?.primary?.text;
  const secMain = colors?.secondary?.main;
  const secText = colors?.secondary?.text;
  const erMain = colors?.error?.main;
  const erText = colors?.error?.text;
  const sucMain = colors?.success?.main;
  const sucText = colors?.success?.text;
  const inMain = colors?.info?.main;
  const inText = colors?.info?.text;
  const disMain = colors?.disabled?.main;
  const disText = colors?.disabled?.text;
  const ghMain = colors?.ghost?.main;
  const ghText = colors?.ghost?.text;

  const baseStyles = {
    fontWeight: font?.weight?.fontW600,
    textTransform: "none",
    borderRadius: radius?.default,
    transition: "all 0.2s ease",
  };

  const cursorPt = isNotAllowed ? "not-allowed" : "pointer";
  const disStyle = {
    backgroundColor: disMain,
    color: disText,
    cursor: cursorPt,
  };

  const variantStyles = {
    primary: {
      backgroundColor: prMain,
      color: prText,
      "&:hover": {
        backgroundColor: secMain,
        cursor: cursorPt,
      },
      "&:disabled": disStyle,
      "&:focus-visible, &.Mui-focusVisible, &:focus": {
        backgroundColor: prMain,
        // outline: "none",
      },
    },
    secondary: {
      backgroundColor: secMain,
      color: secText,
      "&:hover": {
        backgroundColor: prMain,
        cursor: cursorPt,
      },
      "&:disabled": disStyle,
      "&:focus-visible, &.Mui-focusVisible, &:focus": {
        backgroundColor: secMain,
        // outline: "none",
      },
    },
    danger: {
      backgroundColor: erMain,
      color: erText,
      "&:hover": {
        backgroundColor: colors?.error?.dark,
        cursor: cursorPt,
      },
      "&:disabled": disStyle,
      "&:focus-visible, &.Mui-focusVisible, &:focus": {
        backgroundColor: erMain,
        // outline: "none",
      },
    },
    success: {
      backgroundColor: sucMain,
      color: sucText,
      "&:hover": {
        backgroundColor: colors?.success?.dark,
        cursor: cursorPt,
      },
      "&:disabled": disStyle,
      "&:focus-visible, &.Mui-focusVisible, &:focus": {
        backgroundColor: sucMain,
        // outline: "none",
      },
    },
    info: {
      backgroundColor: inMain,
      color: inText,
      "&:hover": {
        backgroundColor: colors?.info?.dark,
        cursor: cursorPt,
      },
      "&:disabled": disStyle,
      "&:focus-visible, &.Mui-focusVisible, &:focus": {
        backgroundColor: inMain,
        // outline: "none",
      },
    },
    disabled: {
      backgroundColor: disMain,
      color: disText,
      opacity: 0.6,
      "&:hover": {
        backgroundColor: disMain,
        cursor: cursorPt,
      },
      "&:focus-visible, &.Mui-focusVisible, &:focus": {
        backgroundColor: disMain,
      },
    },
    ghost: {
      backgroundColor: ghMain,
      color: ghText,
      "&:hover": {
        backgroundColor: colors?.ghost?.dark,
        cursor: cursorPt,
      },
      "&:disabled": disStyle,
      "&:focus-visible, &.Mui-focusVisible, &:focus": {
        backgroundColor: ghMain,
        // outline: "none",
      },
    },
  };

  const sizeStyles = {
    small: {
      padding: "6px 12px",
      fontSize: "0.75rem",
    },
    medium: {
      padding: "8px 16px",
      fontSize: "0.875rem",
    },
    large: {
      padding: "10px 24px",
      fontSize: "1rem",
    },
  };

  const finalSx = {
    ...baseStyles,
    ...(variantStyles[variant] || variantStyles.primary),
    ...(sizeStyles[size] || sizeStyles.medium),
    fullWidth,
    ...sx,
  };

  const button = (
    <MuiButton
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      sx={finalSx}
      startIcon={
        loading ? <CircularProgress size={16} color="inherit" /> : startIcon
      }
      endIcon={!loading ? endIcon : null}
      className={className}
      {...props}
    >
      {loading ? "Loading..." : children}
    </MuiButton>
  );

  return tooltip ? (
    <Tooltip title={tooltip} slotProps={slotProps} arrow>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
};

export default Button;
